import { withSearch } from "@elastic/react-search-ui";
import type { SearchResult } from "@elastic/search-ui";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, Tag } from "components/common";
import { SearchResultBar } from "components/search/buckets/SnomedSearch/common/SnomedResultView/style";
import {
  getActiveStatus,
  getStatusText,
  getTagColor,
  handleNavigate,
  highlightSearchTerm,
  processText
} from "components/search/buckets/utils";
import { useStoreContext } from "context/StoreProvider";
import parse from "html-react-parser";
import { t } from "i18next";
import { Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  chapterString,
  codesystems,
  collectionsString,
  getRaw,
  getSnippet
} from "utils";

import { ResultViewWrapper } from "./style";

const getCodeDisplay = (result: SearchResult) => {
  if (result.code.snippet?.indexOf("<em>") === -1) {
    return result.compact_code.raw.slice(0, 4);
  }
  if (result.code.snippet === null) {
    return getRaw(result, "compact_code")?.slice(0, 4);
  }
  return getSnippet(result, "code");
};

const ResultView = ({ results, searchTerm }: SearchResult) => {
  const navigate = useNavigate();
  const { actions, state } = useStoreContext();

  const { code, scrollRef } = state;

  const { updateCodeValue } = actions;

  const handleScrollOnClick = () => {
    // eslint-disable-next-line unicorn/consistent-function-scoping
    const scrollToElement = () => {
      const element = scrollRef.current[code as string];
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const delay = 500;

    const timeoutId = setTimeout(scrollToElement, delay);

    return () => clearTimeout(timeoutId);
  };

  return (
    <SearchResultBar>
      {results.map((result: SearchResult) => {
        const shouldBeDisabled =
          result.active.raw === "false" &&
          result.code_system.raw !== codesystems.adm;

        const handleClickedResult = () => {
          switch (result.code_system.raw) {
            case codesystems.adm: {
              navigate(
                `${handleNavigate(result.code_system.raw)}/${collectionsString}/${
                  result.compact_code.raw.split(" ")[0]
                }`
              );
              updateCodeValue(result.compact_code.raw.split(" ")[0]);

              break;
            }
            case codesystems.icpc2b: {
              navigate(
                `${handleNavigate(result.code_system.raw)}/${chapterString}/${
                  result.parent_code.raw
                }`
              );
              updateCodeValue(result.parent_code.raw);

              break;
            }
            case codesystems.nlk:
            case codesystems.anatomisk:
            case codesystems.tekstligeresultatverdier:
            case codesystems.undersokelsesmetoder:
            case codesystems.provemateriale: {
              navigate(
                `${handleNavigate(result.code_system.raw)}/${result.code.raw}`
              );
              updateCodeValue(result.code.raw);

              break;
            }
            default: {
              if (shouldBeDisabled) {
                return null;
              }
              navigate(
                `${handleNavigate(result.code_system.raw)}/${chapterString}/${
                  result.code.raw
                }`
              );
              updateCodeValue(result.code.raw);
            }
          }
        };
        return (
          <ResultViewWrapper
            action={!shouldBeDisabled}
            onClick={handleClickedResult}
            $isDisabled={shouldBeDisabled}
            key={`${getRaw(result, "code")}  ${getRaw(result, "code_system")}`}
          >
            {getRaw(result, "code_system") === codesystems.adm ? (
              <div className="d-flex flex-column gap-2">
                <Col xs="auto">
                  <span className="fw-bold pb-1 text-wrap">
                    {getRaw(result, "name_norwegian") && (
                      <span className="pb-1 text-break text-md-normal">
                        {processText(
                          "name_norwegian",
                          searchTerm,
                          false,
                          result
                        )}
                      </span>
                    )}
                  </span>
                </Col>
                <Col
                  className="d-flex gap-2 align-items-center flex-wrap"
                  xs="auto"
                >
                  <span className="fs-6">
                    {getSnippet(result, "code") &&
                      parse(
                        highlightSearchTerm(
                          getCodeDisplay(result),
                          searchTerm
                        ) as string
                      )}
                  </span>
                  {result.status_text.raw && (
                    <span className="d-flex gap-2 flex-wrap">
                      <Tag
                        color="blue"
                        text={
                          getSnippet(result, "parent_code")?.slice(4) as string
                        }
                      />
                      <Tag
                        color={getTagColor(
                          getRaw(result, "status_text") as string
                        )}
                        text={getStatusText(
                          getRaw(result, "status_text") as string
                        )}
                      />
                    </span>
                  )}
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="icon-blue ms-auto"
                  />
                </Col>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                <Col xs="auto" md={5}>
                  {getRaw(result, "name_norwegian") && (
                    <span className="fw-bold pb-1 text-break text-md-normal">
                      {processText("name_norwegian", searchTerm, false, result)}
                    </span>
                  )}
                </Col>
                <Col
                  className="d-flex gap-2 align-items-center flex-wrap"
                  xs="auto"
                >
                  <span className="fs-6">
                    {processText("code", searchTerm, false, result)}
                  </span>
                  {result.replaced_by &&
                    result.replaced_by.raw &&
                    result.code_system.raw && (
                      <span
                        className="d-flex gap-2 align-items-center"
                        role="link"
                        onClick={() => {
                          handleScrollOnClick();
                          updateCodeValue(
                            getRaw(result, "replaced_by") as string
                          );
                          navigate(
                            `${handleNavigate(
                              getRaw(result, "code_system") as string
                            )}/${getRaw(result, "replaced_by") as string}`
                          );
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            handleScrollOnClick();
                            updateCodeValue(
                              getRaw(result, "replaced_by") as string
                            );
                            navigate(
                              `${handleNavigate(
                                getRaw(result, "code_system") as string
                              )}/${getRaw(result, "replaced_by") as string}`
                            );
                          }
                        }}
                        tabIndex={0}
                      >
                        <span className="">{t("general.replacedBy")}:</span>{" "}
                        <Link
                          text={getRaw(result, "replaced_by") as string}
                          href={`#`}
                        />
                      </span>
                    )}
                  <span className="d-flex gap-2 flex-wrap">
                    {getRaw(result, "is_apat") &&
                      getRaw(result, "is_apat") === "true" && (
                        <span className="d-flex flex-column gap-3 flex-md-row">
                          <Tag color={"yellow"} text={codesystems.apat} />
                        </span>
                      )}
                    {result.code_system.raw ? (
                      <Tag color="blue" text={result.code_system.snippet} />
                    ) : null}

                    <Tag
                      color={getActiveStatus(result.active.raw)}
                      text={result.active.raw === "true" ? "Aktiv" : "Inaktiv"}
                    />

                    {/* TEMP, DO NOT GET INTO PROD YET */}
                    {getSnippet(result, "snomed_synonyms") &&
                      (getSnippet(result, "snomed_synonyms")?.indexOf(
                        "<em>"
                      ) !== -1 ||
                        highlightSearchTerm(
                          result.snomed_synonyms.snippet as string,
                          searchTerm
                        ).includes("<em>")) && (
                        <span
                          style={{
                            color: "gray",
                            fontSize: "0.75em",
                            lineHeight: "2em"
                          }}
                        >
                          {"("}
                          {parse(
                            getSnippet(result, "snomed_synonyms")?.indexOf(
                              "<em>"
                            ) === -1
                              ? highlightSearchTerm(
                                  result.snomed_synonyms.snippet as string,
                                  searchTerm
                                )
                              : (getSnippet(
                                  result,
                                  "snomed_synonyms"
                                ) as string)
                          )}
                          {")"}
                        </span>
                      )}
                  </span>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="icon-blue ms-auto"
                  />
                </Col>
              </div>
            )}
          </ResultViewWrapper>
        );
      })}
    </SearchResultBar>
  );
};

export default withSearch(({ searchTerm, results }) => ({
  searchTerm,
  results
}))(ResultView);
